<template>
  <RadioGroup :model-value="mode ?? ''" class="grid grid-cols-1 gap-1.5" data-testid="commitment-fields" @update:model-value="pick">
    <!-- « Soumission type » — THE DEFAULT when the type's template can
         answer, and ALWAYS VISIBLE (his ruling: a section never hides). One
         radio grammar: the chosen face attaches its panel under its OWN row.
         Disabled with its reason as a subline whenever it cannot take the
         answer: no type picked yet, or no standing offer on the type. A
         lookup in flight wears its spinner where the total will land. -->
    <div>
      <label
        class="flex items-center gap-2.5 rounded-lg border p-3"
        :class="[mode === 'template' ? selectedClass : baseClass,
                 pricing.status.value === 'ready' ? 'cursor-pointer' : 'cursor-default opacity-60',
                 templateAttached ? 'rounded-b-none border-b-0' : '']"
        data-testid="commitment-template"
      >
        <RadioGroupItem value="template" :disabled="pricing.status.value !== 'ready'" />
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-medium">{{ templateRowLabel }}</span>
          <span v-if="templateSubline" class="block text-xs text-muted-foreground">
            {{ templateSubline }}
          </span>
        </span>
        <Spinner
          v-if="pricing.status.value === 'loading'"
          class="h-4 w-4 shrink-0"
          data-testid="commitment-template-loading"
        />
        <span
          v-else-if="pricing.status.value === 'ready'"
          class="shrink-0 text-sm font-semibold"
          :class="pricing.total.value == null ? 'text-amber-600 dark:text-amber-500' : ''"
          data-testid="template-total"
        >
          {{ pricing.total.value == null ? '—' : formatCurrency(pricing.total.value) }}
        </span>
      </label>

      <!-- The mini price form: READ structure, ENTER numbers — a price and a
           quantity per line, prefilled where the template answers, both
           overridable. Never structure (no add/remove/reorder — the builder
           exists for that); the numbers belong to the DEAL, never written
           back to the template. -->
      <!-- The panel wears the CHOSEN border with its row — one container;
           the seam between them is an ordinary separator. -->
      <div
        v-if="templateAttached"
        class="grid grid-cols-1 gap-1.5 rounded-b-lg border border-state-ink border-t-border bg-sunken p-3"
        data-testid="template-price-form"
      >
        <!-- Several offers on the type: WHICH one, before the numbers. A
             segmented pick up to three (the explicit-choice law), a select
             past that. The sole offer never asks. -->
        <!-- A lone FORFAIT is asked too: it is never picked silently. -->
        <div v-if="pricing.offers.value.length > 1 || pricing.offers.value.some(o => o.bundledTypes.length)" class="grid gap-1" data-testid="template-offer-pick">
          <p class="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
            {{ t('jobs.agreement.pickOffer') }}
          </p>
          <div v-if="pricing.offers.value.length <= 3" role="radiogroup" class="grid gap-1" :style="`grid-template-columns: repeat(${pricing.offers.value.length}, minmax(0, 1fr))`">
            <button
              v-for="offer in pricing.offers.value"
              :key="offer.id"
              type="button"
              role="radio"
              :aria-checked="pricing.offerId.value === offer.id"
              class="min-w-0 truncate rounded-md border px-2 py-1.5 text-sm font-medium transition-colors"
              :class="pricing.offerId.value === offer.id ? 'border-foreground bg-foreground text-background' : 'border-input bg-background hover:bg-muted'"
              :data-testid="`template-offer-${offer.id}`"
              @click="pricing.pickOffer(offer.id)"
            >
              {{ offer.name }}
              <span v-if="offer.bundledTypes.length" class="ml-1 text-xs opacity-80" :data-testid="`template-offer-forfait-${offer.id}`">· {{ t('proposals.forfait.badge') }}</span>
            </button>
          </div>
          <Select v-else :model-value="pricing.offerId.value ?? ''" @update:model-value="onPickOffer">
            <SelectTrigger class="h-9 bg-background" data-testid="template-offer-select">
              <SelectValue :placeholder="t('jobs.agreement.pickOffer')" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="offer in pricing.offers.value" :key="offer.id" :value="offer.id">
                {{ offer.name }}
                <ForfaitBadge v-if="offer.bundledTypes.length" class="ml-1.5" :own-type-id="jobTemplateId" :bundled-types="offer.bundledTypes" />
              </SelectItem>
            </SelectContent>
          </Select>
          <!-- The chosen forfait says what the intake will ALSO create: the
               other jobs are born at the address, sold with this one. -->
          <p v-if="chosenForfait" class="text-xs text-muted-foreground" data-testid="template-forfait-hint">
            {{ t('proposals.forfait.createsJob', { types: chosenForfait.bundledTypes.map(b => b.name).join(', ') }) }}
          </p>
        </div>
        <!-- ⛔ NO « Choisissez un modèle pour voir ses lignes ». Two buttons
             under a header reading « QUELLE SOUMISSION ? » already say it, and
             the lines appearing when you press one says it again. A sentence
             that narrates the control above it is ink. -->

        <!-- A forfait's sections read PER TYPE: a section stamped with another
             job type wears that type's dot and name — those lines price the
             job the forfait creates. -->
        <template v-for="section in pricing.sections.value" :key="section.key">
          <p
            v-if="section.typeId"
            class="mt-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground first:mt-0"
            :data-testid="`template-section-type-${section.key}`"
          >
            <TypeDot :color="typeColor(section.typeId)" />
            {{ section.typeName ?? section.name }}
            <span v-if="section.name && section.typeName && section.name !== section.typeName" class="font-normal normal-case">· {{ section.name }}</span>
          </p>
          <p v-else-if="section.name" class="mt-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground first:mt-0">
            {{ section.name }}
          </p>
          <div v-for="row in section.rows" :key="row.id" class="flex items-center gap-2 text-sm">
            <span class="min-w-0 flex-1 truncate">
              {{ row.name }}<span v-if="row.unit" class="text-muted-foreground"> ({{ row.unit }})</span>
            </span>
            <Input
              type="number" inputmode="decimal" min="0" step="0.01"
              class="h-8 w-16 shrink-0 bg-background px-2 text-right text-sm"
              :class="row.quantity == null ? warnClass : ''"
              :model-value="row.quantity ?? ''"
              :data-testid="`template-qty-${row.id}`"
              @update:model-value="row.quantity = numberOrNull($event)"
            />
            <Input
              type="number" inputmode="decimal" min="0" step="0.01"
              class="h-8 w-20 shrink-0 bg-background px-2 text-right text-sm"
              :class="row.price == null ? warnClass : ''"
              :model-value="row.price ?? ''"
              :data-testid="`template-price-${row.id}`"
              @update:model-value="row.price = numberOrNull($event)"
            />
          </div>
        </template>
        <div class="mt-0.5 flex items-center justify-between border-t pt-2 text-sm font-semibold">
          <span>{{ t('jobs.agreement.totalBeforeTax') }}</span>
          <span :class="pricing.total.value == null ? 'text-amber-600 dark:text-amber-500' : ''">
            {{ pricing.total.value == null ? '—' : formatCurrency(pricing.total.value) }}
          </span>
        </div>
      </div>
    </div>

    <!-- « Montant convenu » — the custom one-liner, the override. Its panel
         attaches under its own row (the wrapper keeps the radio grid's gap
         OUTSIDE the pair, so row and panel meet on one shared border); full
         fields, no disclosures. -->
    <div>
      <label
        class="flex cursor-pointer items-center gap-2.5 rounded-lg border p-3"
        :class="[mode === 'priced' ? selectedClass : baseClass, mode === 'priced' ? 'rounded-b-none border-b-0' : '']"
        data-testid="commitment-priced"
      >
        <RadioGroupItem value="priced" />
        <span class="text-sm font-medium">{{ t('jobs.agreement.priced') }}</span>
      </label>

      <!-- The fields themselves live in AgreedAmountFields — « Générer »'s
           « montant convenu » mode mounts the SAME form, so the money question
           has one spelling wherever it is asked. -->
      <div
        v-if="mode === 'priced'"
        class="rounded-b-lg border border-state-ink border-t-border bg-sunken p-3"
        data-testid="priced-panel"
      >
        <AgreedAmountFields
          :amount="amount"
          :label="label"
          :taxable="taxable"
          :kind="kind"
          :description="description"
          :label-placeholder="labelPlaceholder"
          :error="error"
          @update:amount="$emit('update:amount', $event)"
          @update:label="$emit('update:label', $event)"
          @update:taxable="$emit('update:taxable', $event)"
          @update:kind="$emit('update:kind', $event)"
          @update:description="$emit('update:description', $event)"
        />
      </div>
    </div>

    <!-- « Sans soumission » — the explicit skip_proposal escape: the name says
         the fact, no sub-line. -->
    <label
      class="flex cursor-pointer items-center gap-2.5 rounded-lg border p-3"
      :class="mode === 'no_proposal' ? selectedClass : baseClass"
      data-testid="commitment-no-proposal"
    >
      <RadioGroupItem value="no_proposal" />
      <span class="text-sm font-medium">{{ t('jobs.agreement.noProposal') }}</span>
    </label>

    <!-- ⚰ « RÉSERVER SEULEMENT » IS GONE (his ruling, 2026-09-20). It booked the
         work while writing NOTHING to the deal — no document, no yes — so the
         job stayed exactly as owed as before. ★ HIS LAW: « the purpose of the
         pipeline is to clear items from the list; if confirming something does
         not clear it, it breaks the purpose. » An answer to « Combien ? » that
         leaves the money question unanswered is not an answer.
         ⚠ It also had a cost nobody was told about: §47's rule counts PLACED
         work as booked whether it is signed or not, so an unsold stop ate a
         crew's day budget, and a dead deal took that capacity with it.
         ⏭ If a « je vous garde le 12 » before the paper ever becomes real, it
         is a HOLD with its own name and its own row on the board — not a fourth
         answer hidden inside the money question. -->
  </RadioGroup>
</template>

<script setup lang="ts">
import { computed, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTemplatePricing, type TemplateFaceStatus, type TemplateLineInput } from '@/composables/useTemplatePricing'
import { useFormatters } from '@/composables/useFormatters'
import type { LineKind } from '@/lib/lineItemKinds'
import { numberOrNull } from '@/lib/number'
import { typeColor } from '@/lib/typeIdentity'

import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import ForfaitBadge from '@/components/shared/ForfaitBadge.vue'
import TypeDot from '@/components/shared/TypeDot.vue'
import AgreedAmountFields from '@/components/shared/AgreedAmountFields.vue'
import Spinner from '@/components/ui/spinner/Spinner.vue'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'

// « Combien ? » — THE money question's one face, shared by both intake forms
// and both win doors (the door and the kebab cannot disagree). Radio rows,
// one grammar: the chosen face attaches its panel under its own row. Dumb on
// purpose about VALUES (the mounting form owns them, vee-validate or refs);
// it owns only the template face's data half (useTemplatePricing) and
// reports it upward.
export type CommitmentMode = 'template' | 'priced' | 'no_proposal'
export interface TemplateFaceState {
  status: TemplateFaceStatus
  valid: boolean
  total: number | null
  lines: TemplateLineInput[]
  // The chosen offer — rides won_template when the type carries several.
  proposalTemplateId: string | null
}

const props = defineProps<{
  // null = nothing chosen yet — the form opens bare, no panel spending space.
  mode: CommitmentMode | null
  amount: number | null
  label: string
  taxable: boolean
  kind: LineKind
  description: string
  // The picked type — drives the template face (null = no template row).
  jobTemplateId: string | null
  // Visible prefill source (the job type's name) when the label is untouched.
  labelPlaceholder?: string | null
  // The mounting form's validation message (doors gate their CTA instead).
  error?: string
}>()

const emit = defineEmits<{
  'update:mode': [value: CommitmentMode]
  'update:amount': [value: number | null]
  'update:label': [value: string]
  'update:taxable': [value: boolean]
  'update:kind': [value: LineKind]
  'update:description': [value: string]
  // The template face's answer, for the form's submit + CTA gate.
  'update:templateState': [value: TemplateFaceState]
}>()

const { t } = useI18n()
const { formatCurrency } = useFormatters()

const baseClass = 'border-input bg-background'
const selectedClass = 'border-state-ink bg-state-wash'
const warnClass = 'border-amber-400 bg-amber-50 dark:border-amber-600 dark:bg-amber-950'

const pricing = useTemplatePricing(toRef(props, 'jobTemplateId'))

// The picked offer when it is a FORFAIT — the form says which jobs it adds.
const chosenForfait = computed(() => {
  const offer = pricing.offers.value.find(o => o.id === pricing.offerId.value)
  return offer?.bundledTypes.length ? offer : null
})

// The row says WHOSE standing offer it is — « Soumission pour X » (his call:
// « Soumission type » alone doesn't describe itself), in EVERY state — the
// label names the OPTION, the subline carries its state (his call: the main
// label never changes). labelPlaceholder IS the type's name at every mount;
// bare fallback only when no type is known.
const templateRowLabel = computed(() =>
  props.labelPlaceholder
    ? t('jobs.agreement.templateFor', { type: props.labelPlaceholder })
    : t('jobs.agreement.template'))

// The disabled row's reason — the control's state, never prose: no type
// picked / THIS type has no standing offer (named, so the line can't read as
// a global claim). Loading and ready say nothing (the spinner and the total
// speak).
const templateSubline = computed(() => {
  if (pricing.status.value === 'none') {
    if (!props.jobTemplateId) return t('jobs.agreement.templateNoType')
    return props.labelPlaceholder
      ? t('jobs.agreement.templateNoneFor', { type: props.labelPlaceholder })
      : t('jobs.agreement.templateNone')
  }
  return null
})

// The chosen face's panel fuses with its row (rounded-b-none ↔ border-t-0 on
// one shared edge) — the wrapper div keeps the radio grid's gap outside.
const templateAttached = computed(() => props.mode === 'template' && pricing.status.value === 'ready')

// « Soumission type » is THE DEFAULT when offered — but only until the user
// states a choice; a late fetch must never yank an explicit answer.
let userTouched = false
watch(pricing.status, (status) => {
  if (status === 'ready' && !userTouched && (props.mode == null || props.mode === 'priced')) {
    emit('update:mode', 'template')
  }
  // The face went away under a template selection (type changed to a
  // template-less one) — fall back to the custom face.
  if (status !== 'ready' && status !== 'loading' && props.mode === 'template') emit('update:mode', 'priced')
})

watch([ pricing.status, pricing.valid, pricing.total, pricing.linesPayload, pricing.offerId ], () => {
  emit('update:templateState', {
    status: pricing.status.value,
    valid: pricing.valid.value,
    total: pricing.total.value,
    lines: pricing.linesPayload.value,
    proposalTemplateId: pricing.offerId.value,
  })
}, { immediate: true })

// The select hands back an untyped value (reka's contract).
const onPickOffer = (value: unknown) => {
  if (typeof value === 'string' && value) void pricing.pickOffer(value)
}

const MODES: readonly CommitmentMode[] = [ 'template', 'priced', 'no_proposal' ]
const pick = (value: unknown) => {
  const mode = MODES.find(m => m === value)
  if (!mode) return
  userTouched = true
  emit('update:mode', mode)
}

</script>
